import { useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { Fragment, useState, type ReactElement } from "react";
import {
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ndd/ui";
import type {
  AlwaysAiUser,
  RdOption,
  WorkspaceSummaryResponse,
} from "../../../../shared/api-types";
import { FEATURE_FLAGS } from "../../../../shared/features";
import { matchesNameSearch } from "@/lib/name-search";
import { canRenameRd } from "@/lib/permissions";
import {
  activeProfileLink,
  activeRdTab,
  canonicalRdId,
  nextOutletKey,
  onRdChatPage,
  rdPageTabs,
  summaryForRd,
  summaryTabCounts,
  switchTarget,
  type RdOutletKey,
} from "@/lib/rd-page-tabs";
import { useFeatureEnabled } from "@/lib/use-features";
import { THEMED_PRESS, THEMED_UTILITY } from "@/lib/always-look";
import type { RdDetailData } from "@/lib/use-rd-detail";
import { RdFrameHeaderContext, RdHeaderStripBody } from "./rd-header-strip";
import {
  RdPageTabsNav,
  RdProfileSubnav,
  type RenderRdTabLink,
} from "./rd-page-tabs-nav";

// The RD page in the single-rail frame (NO-754): the RD header with New chat
// and a switcher that keeps the tab, then the tab row. Chat is not a tab: a
// chat (`/chats/new` or a thread) opens in a frame of its own, with no header
// or tabs, only a link back to the RD page above the conversation. The rail
// lists the RD's chats. The classic frame keeps RdWorkspaceSidebar
// (routes/rd-workspace.tsx branches on the frame).
//
// Every RD-specific read here is keyed by the URL. The header comes from the
// ["rd", <url param>] detail, which has no placeholder, so it never shows the
// previous RD's name. The surface remounts on the RD the URL names, so a
// switch between RDs on the same surface (a same-route param change, where the
// router would keep component instances) cannot carry an open form, a
// selection or a dirty flag across RDs.

export interface RdPageFrameProps {
  rdParam: string;
  detail: RdDetailData | undefined;
  detailFailed: boolean;
  user: AlwaysAiUser | undefined;
  summary: WorkspaceSummaryResponse | undefined;
  rdOptions: RdOption[];
}

export function RdPageFrame(props: RdPageFrameProps): ReactElement {
  const pathname = useLocation().pathname;
  const outletKey = useOutletKey(props.rdParam, props.rdOptions);

  if (onRdChatPage(pathname)) {
    return <RdChatFrame {...props} outletKey={outletKey} />;
  }

  return <RdTabsFrame {...props} pathname={pathname} outletKey={outletKey} />;
}

function RdTabsFrame(
  props: RdPageFrameProps & { pathname: string; outletKey: string }
): ReactElement {
  const { rdParam, rdOptions, pathname, outletKey } = props;
  const metricsEnabled = useFeatureEnabled(FEATURE_FLAGS.supervisorMetrics);
  const rdId = canonicalRdId(rdParam, rdOptions);
  const summary = summaryForRd(props.summary, rdParam, rdId);
  const tabs = rdPageTabs({ metricsEnabled, counts: summaryTabCounts(summary) });
  const active = activeRdTab(pathname);
  const profileLinks = tabs.find((tab) => tab.id === "profile")?.subLinks ?? [];
  const renderLink = tabLinkRenderer(rdParam);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 px-6 pt-5">
        <div className="mx-auto flex max-w-[920px] flex-col">
          <div className="flex items-center gap-4 pb-4">
            <FrameHeader {...props} frameKey={outletKey} />
            <div role="group" aria-label="RD actions" className="flex shrink-0 gap-2">
              <NewChatLink rdParam={rdParam} />
              <RdFrameSwitcher
                currentId={rdId}
                options={rdOptions}
                pathname={pathname}
              />
            </div>
          </div>
          <RdPageTabsNav tabs={tabs} active={active} renderLink={renderLink} />
          {active === "profile" ? (
            <RdProfileSubnav
              links={profileLinks}
              active={activeProfileLink(pathname)}
              renderLink={renderLink}
            />
          ) : null}
        </div>
      </div>
      <FrameOutlet outletKey={outletKey} />
    </div>
  );
}

/**
 * A chat: no RD header and no tabs, only "<RD name> ›" back to the RD page,
 * then the conversation with its own header. The back link's left edge lines
 * up with the chat header's title, which sits 28px in (rd-thread-chat.tsx).
 */
function RdChatFrame({
  rdParam,
  rdOptions,
  detail,
  summary,
  outletKey,
}: RdPageFrameProps & { outletKey: string }): ReactElement {
  const rdId = canonicalRdId(rdParam, rdOptions);
  const name = detail?.name ?? summaryForRd(summary, rdParam, rdId)?.rdName;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <nav aria-label="Back to RD" className="shrink-0 px-[28px] pt-4">
        <Link
          to="/rds/$rdId"
          params={{ rdId: rdParam }}
          activeOptions={{ exact: true, includeSearch: false }}
          className="inline-flex h-7 items-center gap-1 font-sans text-body font-semibold text-muted-foreground transition-colors hover:text-foreground"
        >
          {name ?? (
            <span
              aria-hidden="true"
              className="inline-block h-4 w-24 rounded bg-muted"
            />
          )}
          <span aria-hidden="true">›</span>
        </Link>
      </nav>
      <FrameOutlet outletKey={outletKey} />
    </div>
  );
}

function FrameOutlet({ outletKey }: { outletKey: string }): ReactElement {
  return (
    <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
      <RdFrameHeaderContext.Provider value>
        <Fragment key={outletKey}>
          <Outlet />
        </Fragment>
      </RdFrameHeaderContext.Provider>
    </div>
  );
}

/** New chat sits in the header, beside Switch RD, and shows even without it. */
function NewChatLink({ rdParam }: { rdParam: string }): ReactElement {
  return (
    <Link
      to="/rds/$rdId/chats/new"
      params={{ rdId: rdParam }}
      className={cn(
        "flex h-9 shrink-0 items-center gap-1.5 rounded-[10px] border border-border bg-card px-3 font-sans text-body font-semibold text-foreground shadow-sm transition-colors hover:bg-muted",
        THEMED_PRESS
      )}
    >
      <Plus aria-hidden="true" className="size-[14px]" />
      New chat
    </Link>
  );
}

/** The remount key: the canonical RD id, held while the roster finishes loading. */
function useOutletKey(rdParam: string, rdOptions: RdOption[]): string {
  const [stored, setStored] = useState<RdOutletKey>(() =>
    nextOutletKey(null, rdParam, rdOptions)
  );
  const next = nextOutletKey(stored, rdParam, rdOptions);
  if (next !== stored) setStored(next);

  return next.key;
}

// Tabs mark themselves current from the tab model; `exact` and no search keep
// the router's own active marking (which wins over ours) from lighting
// Overview on every RD page or dropping Meetings on ?meetingNoteId.
function tabLinkRenderer(rdParam: string): RenderRdTabLink {
  return ({ to, current, className, children }) => {
    return (
      <Link
        to={to}
        params={{ rdId: rdParam }}
        activeOptions={{ exact: true, includeSearch: false }}
        aria-current={current ? "page" : undefined}
        className={className}
      >
        {children}
      </Link>
    );
  };
}

function FrameHeader({
  rdParam,
  detail,
  detailFailed,
  user,
  frameKey,
}: RdPageFrameProps & { frameKey: string }): ReactElement | null {
  const queryClient = useQueryClient();
  // A failed detail read is the surface's to report; the header stays empty.
  if (detailFailed) return <div className="min-w-0 flex-1" />;

  if (!detail) {
    return (
      <div aria-hidden="true" className="flex min-w-0 flex-1 items-center gap-4">
        <div className="size-12 shrink-0 rounded-full bg-muted" />
        <div className="h-6 w-48 rounded-md bg-muted" />
      </div>
    );
  }

  return (
    <section aria-label="RD header" className="min-w-0 flex-1">
      <RdHeaderStripBody
        key={frameKey}
        rdId={rdParam}
        canEdit={user ? canRenameRd(user) : false}
        initialName={detail.name}
        sessionCount={detail.sessionCount}
        createdAt={detail.createdAt}
        supervisorName={detail.supervisorName}
        healthieLink={detail.healthieLink}
        // The rail and the switcher name RDs from rd-options (and the
        // whole-roster viewer's team from ["rds", …]); the summary carries
        // the name the crumbs and threads read.
        onRenamed={() => {
          void queryClient.invalidateQueries({ queryKey: ["rd-options"] });
          void queryClient.invalidateQueries({ queryKey: ["rds"] });
          void queryClient.invalidateQueries({
            queryKey: ["workspace-summary", rdParam],
          });
        }}
      />
    </section>
  );
}

/**
 * Switching RD keeps the tab (switchTarget). cmdk keys rows by `value`, so each
 * row's value is the RD id (two RDs can share a name) and the filter reads only
 * `keywords`, as in roster-scope-selector.tsx.
 */
function RdFrameSwitcher({
  currentId,
  options,
  pathname,
}: {
  currentId: string;
  options: RdOption[];
  pathname: string;
}): ReactElement | null {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (options.length < 2) return null;

  const current = options.find((rd) => rd.id === currentId);

  const select = (rd: RdOption): void => {
    setOpen(false);
    if (rd.id !== currentId) void navigate(switchTarget(pathname, rd));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* Radix's PopoverTrigger injects aria-expanded/aria-controls at
            runtime; declaring role="combobox" statically trips a11y lint
            without them. */}
        <button
          type="button"
          aria-label={current ? `Switch RD — currently ${current.name}` : "Switch RD"}
          className={cn(
            "flex h-9 shrink-0 items-center gap-2 rounded-[10px] border border-border bg-card px-3 font-sans text-body font-semibold text-foreground shadow-sm",
            THEMED_UTILITY
          )}
        >
          Switch RD
          <ChevronsUpDown className="size-[14px] text-subtle" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[280px] p-0">
        <RdSwitcherList currentId={currentId} options={options} onSelect={select} />
      </PopoverContent>
    </Popover>
  );
}

function RdSwitcherList({
  currentId,
  options,
  onSelect,
}: {
  currentId: string;
  options: RdOption[];
  onSelect: (rd: RdOption) => void;
}): ReactElement {
  return (
    <Command filter={(_value, search, keywords) => matchesNameSearch(search, keywords)}>
      <CommandInput placeholder="Find an RD…" />
      <CommandList>
        <CommandEmpty>No RD found.</CommandEmpty>
        <CommandGroup>
          {options.map((rd) => (
            <CommandItem
              key={rd.id}
              value={rd.id}
              keywords={[rd.name]}
              onSelect={() => onSelect(rd)}
            >
              <Check
                className={cn(
                  "size-4",
                  rd.id === currentId ? "opacity-100" : "opacity-0"
                )}
              />
              <span className="flex-1 truncate">{rd.name}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
